"use client";

/**
 * CollabV3WorksCard 컴포넌트
 * @description 상세의 작품 카드(앱 `CollabDetailWorksCard`) — 제목 · 칩(마감 · `Link • Photo` ·
 *   `1 work each`) · 작품 칸 · `4 works posted`. 고른 작품에는 `Picked`, 마무리 글로 골랐으면
 *   `Picked by {주최자}`.
 *
 *   **맛보기만 보인다.** 칸은 4개까지(고른 작품이 먼저)이고, 그 아래 `See the rest in the app` 하나.
 *   칸을 누르면 앱의 그 작품이 열린다 — 웹에는 작품 목록 · 작품 상세가 없다. 작품 올리기 · 하트 ·
 *   댓글도 앱에서 한다. 마감 날짜를 표시 시간대로 찍어야 해서 클라이언트 컴포넌트다.
 * @example <CollabV3WorksCard works={detail.works} previews={previews} hostName="노바" subject={subject} />
 */
import { Card } from "@fanwork-web/design-system";
import { memo, useMemo } from "react";

import CollabV3AppOverlay from "./CollabV3AppOverlay";
import CollabV3Badge from "./CollabV3Badge";
import {
  COLLAB_V3_TILE_SIZES,
  collabV3CardStyles as styles,
} from "./CollabV3Card.styles";
import type { CollabV3WorksCardProps } from "./CollabV3Card.types";
import CollabV3SeeInApp from "./CollabV3SeeInApp";
import { useCollabV3TimeZone } from "./CollabV3TimeZone";

import { COLLAB_V3_COPY } from "../../constants/collabV3Copy";
import { COLLAB_V3_CARD_COPY } from "../../constants/collabV3CardCopy";
import { COLLAB_V3_TEASER } from "../../constants/collabV3Teaser";
import { collabV3AppPaths } from "../../utils/collabV3AppPaths";
import { formatDay } from "../../utils/collabV3Format";
import { AppImage, IMAGE_PLACEHOLDER } from "@/shared/ui";

const copy = COLLAB_V3_CARD_COPY.works;

const CollabV3WorksCard = memo<CollabV3WorksCardProps>(
  ({ works, previews, hostName, subject }) => {
    const timeZone = useCollabV3TimeZone();
    const listPath = useMemo(
      () => collabV3AppPaths(subject.collabId).works,
      [subject.collabId],
    );
    const deadline = useMemo(
      () =>
        works.isClosed
          ? copy.closed
          : copy.until(formatDay(works.deadline, timeZone)),
      [works.isClosed, works.deadline, timeZone],
    );
    const tiles = useMemo(
      () => previews.slice(0, COLLAB_V3_TEASER.works),
      [previews],
    );

    return (
      <Card
        slot={
          <div className={styles.content}>
            <h3 className={styles.title}>{copy.title}</h3>
            <div className={styles.works.chips}>
              <CollabV3Badge text={deadline} tone={works.isClosed ? "inverse" : "neutral"} />
              <CollabV3Badge text={copy.formats} tone="neutral" />
              {works.allowMultipleWorks ? null : (
                <CollabV3Badge text={copy.onePerPerson} tone="neutral" />
              )}
            </div>
            {tiles.length > 0 ? (
              <ul className={styles.works.tiles}>
                {tiles.map((work) => (
                  <li key={work.workId} className={styles.works.tile}>
                    <AppImage
                      uri={work.imageUrl ?? ""}
                      fallback={IMAGE_PLACEHOLDER}
                      altText={work.authorName ?? ""}
                      sizes={COLLAB_V3_TILE_SIZES}
                    />
                    {work.isSelected ? (
                      <span className={styles.works.tileBadge}>
                        <CollabV3Badge text={COLLAB_V3_COPY.thread.selected} tone="brand" />
                      </span>
                    ) : null}
                    <CollabV3AppOverlay
                      subject={subject}
                      placement="works"
                      path={work.appPath}
                      label={`${work.authorName ?? copy.title}, ${COLLAB_V3_COPY.app.openApp}`}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
            <div className={styles.works.footer}>
              <span className={styles.meta}>
                {works.workCount > 0 ? copy.count(works.workCount) : copy.empty}
              </span>
              {works.hasAnnounced ? (
                <CollabV3Badge text={copy.selectedBy(hostName)} tone="highlight" />
              ) : null}
            </div>
            {works.workCount > 0 ? (
              <CollabV3SeeInApp subject={subject} placement="works" path={listPath} />
            ) : null}
          </div>
        }
      />
    );
  },
);

CollabV3WorksCard.displayName = "CollabV3WorksCard";

export default CollabV3WorksCard;
